import React, { useState, useRef, useEffect } from 'react';
import { Card, POI } from '../services/poiService';
import RichTextDisplay from './RichTextDisplay';
import { useAuth } from '../contexts/AuthContext';
import { User } from 'lucide-react';
import { getUserDisplayName } from '../utils/userUtils';
import { getReadableCategoryName } from '../utils/categoryUtils';
import { getYouTubeVideoId, getTikTokVideoId, TIKTOK_ASPECT_RATIO, TIKTOK_MAX_WIDTH } from '../utils/videoUrlHelpers';

interface CardGalleryProps {
  cards: Card[];
  poi?: POI;
  onCardDelete?: (cardIdV2: string) => void;
  onCardEdit?: (card: Card) => void;
  onUserClick?: (userId: string) => void;
  customOrder?: string[]; // Array of card IDs in desired order
}

const CardGallery: React.FC<CardGalleryProps> = ({ cards, poi, onCardDelete, onCardEdit, onUserClick, customOrder }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Apply custom order if provided
  const orderedCards = React.useMemo(() => {
    if (!customOrder || customOrder.length === 0) {
      return cards;
    }
    
    // Create a map for quick lookup
    const cardMap = new Map(cards.map(card => [card.id_v2, card]));
    
    // Order cards according to customOrder, then append any cards not in customOrder
    const ordered: Card[] = [];
    const orderedIds = new Set<string>();
    
    customOrder.forEach(id => {
      const card = cardMap.get(id);
      if (card) {
        ordered.push(card);
        orderedIds.add(id);
      }
    });
    
    // Append any cards not in the custom order
    cards.forEach(card => {
      if (!orderedIds.has(card.id_v2)) {
        ordered.push(card);
      }
    });
    
    return ordered;
  }, [cards, customOrder]);

  // Handle touch events for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentIndex < orderedCards.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
    if (isRightSwipe && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // Navigate to next card
  const nextCard = () => {
    if (currentIndex < orderedCards.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  // Navigate to previous card
  const prevCard = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // Auto-scroll the carousel container
  useEffect(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = container.clientWidth;
      container.scrollTo({
        left: currentIndex * cardWidth,
        behavior: 'smooth'
      });
    }
  }, [currentIndex]);

  if (orderedCards.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <p className="text-gray-500 dark:text-gray-400 dark:text-gray-500">No information cards available for this location.</p>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col h-full">
      {/* POI Pending Notice */}
      {poi?.validation_status === 'PENDING' && (
        <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex-shrink-0">
          <div className="flex items-center">
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-sm font-medium text-yellow-800">
                Location Pending Approval
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                This location is awaiting validation. Content may be updated after review.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Arrows - positioned outside card */}
      {orderedCards.length > 1 && (
        <>
          <button
            onClick={prevCard}
            disabled={currentIndex === 0}
            className={`hidden sm:flex absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-12 p-3 rounded-full shadow-lg transition-all z-20 ${
              currentIndex === 0 
                ? 'bg-gray-300 text-gray-500 dark:text-gray-400 dark:text-gray-500 cursor-not-allowed' 
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-800 hover:shadow-xl'
            }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            onClick={nextCard}
            disabled={currentIndex === orderedCards.length - 1}
            className={`hidden sm:flex absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-12 p-3 rounded-full shadow-lg transition-all z-20 ${
              currentIndex === orderedCards.length - 1 
                ? 'bg-gray-300 text-gray-500 dark:text-gray-400 dark:text-gray-500 cursor-not-allowed' 
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-800 hover:shadow-xl'
            }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Main Card Display */}
      <div 
        className="relative overflow-hidden rounded-lg flex-1 min-h-0"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div 
          ref={scrollContainerRef}
          className="flex transition-transform duration-300 ease-in-out h-full"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {orderedCards.map((card) => (
            <div
              key={card.id}
              className="w-full flex-shrink-0 h-full"
            >
              <CardItem 
                card={card} 
                poi={poi}
                onCardDelete={onCardDelete}
                onCardEdit={onCardEdit}
                onUserClick={onUserClick}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Individual Card Component
const CardItem: React.FC<{
  card: Card;
  poi?: POI;
  onCardDelete?: (cardIdV2: string) => void;
  onCardEdit?: (card: Card) => void;
  onUserClick?: (userId: string) => void;
}> = ({ card, poi, onCardDelete, onCardEdit, onUserClick }) => {
  const { user } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Check if this card is pending - should always show for pending cards
  const showCardPendingNotice = card.validation_status === 'PENDING';
  const showPOIPendingNotice = poi?.validation_status === 'PENDING';
  const showEditDeleteButtons = user && card.user_id === user.id;

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCardEdit?.(card);
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this card? This action cannot be undone.')) {
      setIsDeleting(true);
      try {
        await onCardDelete?.(card.id_v2);
      } catch (error) {
        console.error('Failed to delete card:', error);
        alert('Failed to delete card. Please try again.');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 relative h-full flex flex-col min-h-0 overflow-hidden">
      {/* Top-right controls container */}
      <div className="absolute top-2 right-2 z-10 flex flex-col items-end space-y-1">
        {/* Card PENDING Validation Notice */}
        {showCardPendingNotice && (
          <span className="inline-flex items-center px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
            <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="hidden sm:inline">CARD PENDING</span>
            <span className="sm:hidden">!</span>
          </span>
        )}

        {/* POI PENDING Validation Notice - only show if card is not already pending */}
        {!showCardPendingNotice && showPOIPendingNotice && (
          <span className="inline-flex items-center px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
            <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="hidden sm:inline">LOCATION PENDING</span>
            <span className="sm:hidden">⚠</span>
          </span>
        )}

        {/* Edit/Delete Buttons for owned cards */}
        {showEditDeleteButtons && (
          <div className="flex space-x-1">
          <button
            onClick={handleEdit}
            disabled={isDeleting}
            className="inline-flex items-center px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800 border border-primary-200 hover:bg-primary-200 transition-colors disabled:opacity-50 touch-target"
            title="Edit card"
          >
            <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <span className="hidden sm:inline">Edit</span>
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="inline-flex items-center px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200 hover:bg-red-200 transition-colors disabled:opacity-50 touch-target"
            title="Delete card"
          >
            {isDeleting ? (
              <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 sm:mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            )}
            <span className="hidden sm:inline">Delete</span>
          </button>
          </div>
        )}
      </div>
      
      {/* Image with category overlay - Android style */}
      {card.image_url && (
        <div className="relative flex-shrink-0 card-image-container">
          <img 
            src={card.image_url} 
            alt="Card image"
            className="w-full object-cover card-image"
            loading="lazy"
            crossOrigin="anonymous"
            referrerPolicy="strict-origin-when-cross-origin"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          {/* Category overlay on image - like Android */}
          {poi?.category && (
            <div className="absolute bottom-3 left-3">
              <span className="inline-block bg-black bg-opacity-70 text-white text-xs sm:text-sm px-3 py-1.5 rounded-full font-medium backdrop-blur-sm">
                {getReadableCategoryName(poi.category)}
              </span>
            </div>
          )}
        </div>
      )}
      
      {/* Scrollable content area - Android style */}
      <div className="flex-1 overflow-y-auto min-h-0 custom-scrollbar p-3 sm:p-4">
        {/* Text Content */}
        {card.text && (
          <div className="mb-3">
            <RichTextDisplay 
              content={card.text} 
              className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm sm:text-base"
              maxLines={5}
              enableShowMore={true}
            />
          </div>
        )}
        
        {/* Audio */}
        {card.audio_url && (
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              🎵 Audio Content
            </label>
            <audio 
              controls 
              className="w-full rounded-lg"
              style={{ height: '40px', maxHeight: '40px' }}
              preload="metadata"
            >
              <source src={card.audio_url} type="audio/mpeg" />
              <source src={card.audio_url} type="audio/wav" />
              <source src={card.audio_url} type="audio/ogg" />
              Your browser does not support the audio element.
            </audio>
          </div>
        )}
        
        {/* Video */}
        {card.video_url && (
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              🎬 Video Content
            </label>
            {(() => {
              const youtubeId = getYouTubeVideoId(card.video_url);
              const tiktokId = getTikTokVideoId(card.video_url);
              
              if (youtubeId) {
                return (
                  <div className="relative w-full rounded-lg overflow-hidden" style={{ paddingBottom: '56.25%', height: 0 }}>
                    <iframe
                      src={`https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1&fs=1`}
                      title="YouTube video player"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      className="absolute top-0 left-0 w-full h-full border-0"
                    />
                  </div>
                );
              } else if (tiktokId) {
                return (
                  <div className="relative w-full rounded-lg overflow-hidden" style={{ paddingBottom: TIKTOK_ASPECT_RATIO, height: 0, maxWidth: TIKTOK_MAX_WIDTH, margin: '0 auto' }}>
                    <iframe
                      src={`https://www.tiktok.com/embed/v2/${tiktokId}`}
                      title="TikTok video player"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      className="absolute top-0 left-0 w-full h-full border-0"
                    />
                  </div>
                );
              } else {
                // Fallback for non-YouTube/TikTok videos
                return (
                  <video 
                    controls 
                    className="w-full rounded-lg"
                    style={{ maxHeight: 'min(200px, 30vh)' }}
                    preload="metadata"
                  >
                    <source src={card.video_url} type="video/mp4" />
                    <source src={card.video_url} type="video/webm" />
                    <source src={card.video_url} type="video/ogg" />
                    Your browser does not support the video element.
                  </video>
                );
              }
            })()}
          </div>
        )}

        {/* Creator Information */}
        <div className="mt-auto pt-3 border-t border-gray-100 dark:border-gray-700">
          <button 
            onClick={() => onUserClick?.(card.user_id)}
            className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500 hover:text-primary-600 transition-colors group w-full text-left"
          >
            <User className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-primary-500" />
            <span>
              Created by{' '}
              <span className="font-medium">
                {getUserDisplayName({
                  displayName: card.creator_display_name,
                  firstName: card.creator_first_name,
                  lastName: card.creator_last_name
                })}
              </span>
            </span>
          </button>
        </div>
      </div>

    </div>
  );
};

export default CardGallery;
